/**
 * Zachytenie snímky aktuálnej obrazovky pomocou html2canvas.
 *
 * Používa sa pri hlásení chyby. Snímka sa nikam neukladá — putuje priamo
 * do prílohy e-mailu, preto je dôležité držať ju malú.
 *
 * @module screenshot
 */

/** Strop pre výsledný data URL. Server odmieta nad 2 MB, tu držíme rezervu. */
const MAX_BYTES = 1.5 * 1024 * 1024;

/** html2canvas vie na starších Androidoch visieť — po tomto čase to vzdáme. */
const TIMEOUT_MS = 8000;

/** Postupné zhoršovanie kvality, kým sa snímka nezmestí do limitu. */
const COMPRESSION_STEPS = [
  { scale: 1, quality: 0.7 },
  { scale: 1, quality: 0.5 },
  { scale: 0.75, quality: 0.5 },
  { scale: 0.6, quality: 0.45 },
  { scale: 0.5, quality: 0.4 },
];

/** Približná veľkosť dát v data URL (base64 = 4 znaky na 3 bajty). */
function approxBytes(dataUrl) {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

function downscale(canvas, scale) {
  const maly = document.createElement("canvas");
  maly.width = Math.max(1, Math.round(canvas.width * scale));
  maly.height = Math.max(1, Math.round(canvas.height * scale));
  maly.getContext("2d").drawImage(canvas, 0, 0, maly.width, maly.height);
  return maly;
}

/**
 * Prevedie canvas na JPEG data URL a postupne zhoršuje kvalitu, kým sa
 * nezmestí do limitu. JPEG, nie PNG — snímka dashboardu má v PNG bežne
 * 1–3 MB, v JPEG q=0.7 typicky 150–400 kB.
 */
function compressToJpeg(canvas) {
  for (const step of COMPRESSION_STEPS) {
    const cielovy = step.scale === 1 ? canvas : downscale(canvas, step.scale);

    let dataUrl;
    try {
      dataUrl = cielovy.toDataURL("image/jpeg", step.quality);
    } catch {
      // SecurityError — canvas je "otrávený" obrázkom z cudzej domény bez CORS.
      throw new Error("TAINTED_CANVAS");
    }

    if (approxBytes(dataUrl) <= MAX_BYTES) return dataUrl;
  }

  throw new Error("SCREENSHOT_TOO_LARGE");
}

async function capture() {
  // Dynamický import — html2canvas (~200 kB) sa načíta až pri prvom použití,
  // nie do hlavného bundle.
  const { default: html2canvas } = await import("html2canvas");

  const sirka = document.documentElement.clientWidth;
  const vyska = document.documentElement.clientHeight;

  const canvas = await html2canvas(document.body, {
    useCORS: true,
    // Radšej vynechať cudzí obrázok, než mať canvas, z ktorého sa nedá čítať.
    allowTaint: false,
    logging: false,
    // Pozadie stránky (v tmavom režime tmavé), inak by priehľadné miesta
    // na snímke vyšli biele.
    backgroundColor: getComputedStyle(document.body).backgroundColor || "#ffffff",
    scale: Math.min(window.devicePixelRatio || 1, 1.5),
    // Len viditeľný výrez, nie celý dokument.
    x: window.scrollX,
    y: window.scrollY,
    width: sirka,
    height: vyska,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    windowWidth: sirka,
    windowHeight: vyska,
    // Prvky označené data-html2canvas-ignore sa do snímky nedostanú
    // (tlačidlo hlásenia a jeho modál).
    ignoreElements: (el) => el?.dataset?.html2canvasIgnore === "true",
  });

  return compressToJpeg(canvas);
}

/**
 * Zachytí viditeľný výrez obrazovky ako JPEG data URL.
 *
 * @returns {Promise<string>} `data:image/jpeg;base64,...`
 * @throws {Error} s kódom `TAINTED_CANVAS`, `SCREENSHOT_TOO_LARGE`, `TIMEOUT`
 *                 alebo pôvodnou chybou html2canvas
 */
export async function captureViewport() {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("TIMEOUT")), TIMEOUT_MS);
  });

  try {
    return await Promise.race([capture(), timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Slovenské vysvetlenie, prečo sa snímka nepodarila — pre používateľa. */
export function screenshotErrorMessage(error) {
  switch (error?.message) {
    case "TIMEOUT":
      return "Snímku obrazovky sa nepodarilo vytvoriť včas.";
    case "TAINTED_CANVAS":
      return "Snímku obrazovky sa nepodarilo vytvoriť kvôli obrázku z externého zdroja.";
    case "SCREENSHOT_TOO_LARGE":
      return "Snímka obrazovky je príliš veľká.";
    default:
      return "Snímku obrazovky sa nepodarilo vytvoriť.";
  }
}
